import React, { useState } from 'react';
import {
  Typography,
  Box,
  TextField,
  Divider,
  InputAdornment,
  Grid,
} from '@mui/material';
import SettingsIcon from '@mui/icons-material/Settings';
import { getOptimalParametersForTaskCount } from '../../utils/parameterUtils';

interface ParameterPanelProps {
  initialValues?: {
    taskCount?: number;
    qosProperties?: number;
    iterations?: number;
    initialPopulation?: number;
    selectionRate?: number;
    crossoverRate?: number;
    mutationRate?: number;
    memeplexCount?: number;
    numberOfRuns?: number;
  };
  onParametersChange?: (params: {
    taskCount: number;
    qosProperties: number;
    iterations: number;
    initialPopulation: number;
    selectionRate: number;
    crossoverRate: number;
    mutationRate: number;
    memeplexCount: number;
    numberOfRuns?: number;
  }) => void;
}

const ParameterPanel: React.FC<ParameterPanelProps> = ({ initialValues, onParametersChange }) => {
  // Initialize with optimal parameters based on taskCount if no initialValues provided
  const initialTaskCount = initialValues?.taskCount ?? 10;
  const optimalParams = getOptimalParametersForTaskCount(initialTaskCount);

  const [parameters, setParameters] = useState({
    taskCount: initialTaskCount,
    qosProperties: initialValues?.qosProperties ?? 9,
    iterations: initialValues?.iterations ?? Math.floor(initialTaskCount / 10) + 40, // iterations = (workflow tasks / 10) + 40
    initialPopulation: initialValues?.initialPopulation ?? (initialTaskCount + 40), // initial population = workflow tasks + 40
    selectionRate: initialValues?.selectionRate ?? 60, // 60% for GA/Cultural (SFLAGA uses tournament directly)
    crossoverRate: initialValues?.crossoverRate ?? optimalParams.crossoverRate,
    mutationRate: Math.round(initialValues?.mutationRate ?? optimalParams.mutationRate), // Always rounded
    memeplexCount: initialValues?.memeplexCount ?? optimalParams.memeplexCount,
    numberOfRuns: initialValues?.numberOfRuns ?? 10, // number of runs = 10
  });

  // Update parameters when initialValues change (for preset loading and complete run updates)
  React.useEffect(() => {
    if (initialValues) {
      // Use all provided values - the context already calculates optimal parameters
      // Only update if values actually changed to avoid unnecessary re-renders
      setParameters(prev => {
        // Check if taskCount changed - if so, use all new values (they include optimal params)
        if (initialValues.taskCount !== undefined && initialValues.taskCount !== prev.taskCount) {
          const optimal = getOptimalParametersForTaskCount(initialValues.taskCount);
          return {
            taskCount: initialValues.taskCount,
            qosProperties: initialValues.qosProperties ?? prev.qosProperties,
            iterations: Math.floor(initialValues.taskCount / 10) + 40, // iterations = (workflow tasks / 10) + 40
            initialPopulation: initialValues.initialPopulation ?? (initialValues.taskCount + 40), // initial population = workflow tasks + 40
            selectionRate: initialValues.selectionRate ?? prev.selectionRate,
            crossoverRate: initialValues.crossoverRate ?? optimal.crossoverRate,
            mutationRate: Math.round(initialValues.mutationRate ?? optimal.mutationRate), // Always rounded
            memeplexCount: initialValues.memeplexCount ?? optimal.memeplexCount,
            numberOfRuns: 10, // number of runs = 10
          };
        }
        // Otherwise, merge with existing values
        return {
          ...prev,
          ...initialValues,
          // Ensure mutation rate is always rounded
          mutationRate: initialValues.mutationRate !== undefined 
            ? Math.round(initialValues.mutationRate) 
            : prev.mutationRate,
        };
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialValues?.taskCount, initialValues?.iterations, initialValues?.initialPopulation, 
      initialValues?.crossoverRate, initialValues?.mutationRate, initialValues?.memeplexCount]);

  const handleChange = (field: string) => (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    let value = parseInt(event.target.value, 10) || 0;
    
    // Round mutation rate to nearest integer (no decimal values)
    if (field === 'mutationRate') {
      value = Math.round(value);
    }
    
    let newParams = { ...parameters, [field]: value };
    
    // If taskCount changes, auto-adjust other parameters based on problem size
    if (field === 'taskCount') {
      const optimal = getOptimalParametersForTaskCount(value);
      newParams = {
        ...newParams,
        ...optimal,
        iterations: Math.floor(value / 10) + 40, // iterations = (workflow tasks / 10) + 40
        initialPopulation: value + 40, // initial population = workflow tasks + 40
        numberOfRuns: 10, // number of runs = 10
      };
    }
    
    setParameters(newParams);
    if (onParametersChange) {
      onParametersChange(newParams);
    }
  };

  // Initial notification
  React.useEffect(() => {
    if (onParametersChange) {
      onParametersChange(parameters);
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <Box>
      <Box sx={{ display: 'flex', alignItems: 'center', mb: { xs: 2, md: 3 } }}>
        <SettingsIcon sx={{ mr: 1, color: 'primary.main', fontSize: { xs: '1.25rem', md: '1.5rem' } }} />
        <Typography variant="h6" component="h2" sx={{ fontSize: { xs: '1.1rem', md: '1.25rem' } }}>
          Parameters
        </Typography>
      </Box>

      <Divider sx={{ mb: 3 }} />

      {/* Common Parameters */}
      <Typography variant="subtitle2" sx={{ mb: 2, fontWeight: 600, fontSize: { xs: '0.875rem', md: '1rem' } }}>
        Common Parameters
      </Typography>
      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid item xs={12} sm={6}>
          <TextField
            label="Workflow Tasks"
            type="number"
            value={parameters.taskCount}
            onChange={handleChange('taskCount')}
            size="small"
            fullWidth
            inputProps={{ min: 1, max: 1000 }}
          />
        </Grid>
        <Grid item xs={12} sm={6}>
          <TextField
            label="QoS Properties"
            type="number"
            value={parameters.qosProperties}
            onChange={handleChange('qosProperties')}
            size="small"
            fullWidth
            inputProps={{ min: 1, max: 20 }}
          />
        </Grid>
        <Grid item xs={12} sm={6}>
          <TextField
            label="Iterations"
            type="number"
            value={parameters.iterations}
            onChange={handleChange('iterations')}
            size="small"
            fullWidth
            inputProps={{ min: 1, max: 10000 }}
          />
        </Grid>
        <Grid item xs={12} sm={6}>
          <TextField
            label="Initial Population"
            type="number"
            value={parameters.initialPopulation}
            onChange={handleChange('initialPopulation')}
            size="small"
            fullWidth
            inputProps={{ min: 10, max: 10000 }}
          />
        </Grid>
      </Grid>

      <Divider sx={{ mb: 3 }} />

      {/* GA Parameters */}
      <Typography variant="subtitle2" sx={{ mb: 2, fontWeight: 600, fontSize: { xs: '0.875rem', md: '1rem' } }}>
        Genetic Algorithm (GA)
      </Typography>
      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid item xs={12} sm={6}>
          <TextField
            label="Selection Rate"
            type="number"
            value={parameters.selectionRate}
            onChange={handleChange('selectionRate')}
            size="small"
            fullWidth
            InputProps={{
              endAdornment: <InputAdornment position="end">%</InputAdornment>,
            }}
            inputProps={{ min: 0, max: 100 }}
          />
        </Grid>
        <Grid item xs={12} sm={6}>
          <TextField
            label="Crossover Rate"
            type="number"
            value={parameters.crossoverRate}
            onChange={handleChange('crossoverRate')}
            size="small"
            fullWidth
            InputProps={{
              endAdornment: <InputAdornment position="end">%</InputAdornment>,
            }}
            inputProps={{ min: 0, max: 100 }}
          />
        </Grid>
        <Grid item xs={12} sm={6}>
          <TextField
            label="Mutation Rate"
            type="number"
            value={Math.round(parameters.mutationRate)}
            onChange={handleChange('mutationRate')}
            size="small"
            fullWidth
            InputProps={{
              endAdornment: <InputAdornment position="end">%</InputAdornment>,
            }}
            inputProps={{ min: 0, max: 100, step: 1 }}
          />
        </Grid>
      </Grid>

      <Divider sx={{ mb: 3 }} />

      {/* SFLA Parameters */}
      <Typography variant="subtitle2" sx={{ mb: 2, fontWeight: 600, fontSize: { xs: '0.875rem', md: '1rem' } }}>
        SFLA Parameters
      </Typography>
      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid item xs={12} sm={6}>
          <TextField
            label="Memeplex Count"
            type="number"
            value={parameters.memeplexCount}
            onChange={handleChange('memeplexCount')}
            size="small"
            fullWidth
            inputProps={{ min: 1, max: 100 }}
          />
        </Grid>
      </Grid>

      <Divider sx={{ mb: 3 }} />

      {/* Statistical Analysis Parameters */}
      <Typography variant="subtitle2" sx={{ mb: 2, fontWeight: 600, fontSize: { xs: '0.875rem', md: '1rem' } }}>
        Statistical Analysis
      </Typography>
      <Grid container spacing={2}>
        <Grid item xs={12} sm={6}>
          <TextField
            label="Number of Runs"
            type="number"
            value={parameters.numberOfRuns}
            onChange={handleChange('numberOfRuns')}
            size="small"
            fullWidth
            inputProps={{ min: 1, max: 100 }}
            helperText="Multiple runs for statistical analysis"
          />
        </Grid>
      </Grid>
    </Box>
  );
};

export default ParameterPanel;

